import { FC, useEffect, useState } from 'react';
import { ProductMaster, fetchProductMaster } from '../../api/productMasters';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

interface MastersDetailPanelProps {
  id: string;
  onBack: () => void;
  servicesOnly?: boolean;
  onEdit?: () => void;
}

const MastersDetailPanel: FC<MastersDetailPanelProps> = ({ id, onBack, servicesOnly = false, onEdit }) => {
  const [data, setData] = useState<ProductMaster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchProductMaster(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load master product.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading master product...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? (servicesOnly ? 'Service not found.' : 'Master product not found.')}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to {servicesOnly ? 'services' : 'master products'}
          </button>
        </div>
      </div>
    );
  }

  const originalCreated = data.originalCreatedAt
    ? new Date(data.originalCreatedAt).toLocaleString()
    : '—';

  const formatType = (value: ProductMaster['type']) => {
    if (!value) {
      return 'Regular';
    }

    const label = value.replace(/[_-]+/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const renderDescription = (value?: string | null) => {
    const text = (value ?? '').trim();
    if (!text) {
      return '—';
    }

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(text);

    if (hasHtmlTags) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }

    return text;
  };

  return (
    <div className="flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">

      <PageHeaderWithBack
        title={data.name}
        description={<p className="mt-1 max-w-2xl text-sm text-slate-500">
          ERP ID {data.erpId} · Article {data.articleNumber}
        </p>}
        backHref="/order-management"
        onBack={onBack}
        backLabel={`Back to ${servicesOnly ? 'services' : 'master products'}`}
        actions={servicesOnly && onEdit ? (
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-primary"
            onClick={onEdit}
          >
            Edit service
          </button>
        ) : undefined}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Metadata</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</dt>
              <dd>{data.erpId}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Article Number</dt>
              <dd>{data.articleNumber}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Brand</dt>
              <dd>{data.brand?.name ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Description</dt>
              <dd>{renderDescription(data.description)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Variant Template</dt>
              <dd>{data.variantTemplate?.name ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Categories</dt>
              <dd>
                {(() => {
                  const names = Array.isArray(data.categories) && data.categories.length
                    ? data.categories.map((c) => c.name).filter(Boolean)
                    : Array.isArray((data as any).category_ids) && (data as any).category_ids.length && Array.isArray((data as any).categories)
                      ? (data as any).categories
                        .filter((c: any) => (data as any).category_ids.includes(Number(c?.erpId)))
                        .map((c: any) => c.name)
                        .filter(Boolean)
                      : [];
                  return names.length ? names.join(', ') : '—';
                })()}
              </dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Type</dt>
              <dd>{formatType(data.type)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Source</dt>
              <dd>{data.isCreatedByWorkshopManagement ? 'Workshop management' : 'ERP'}</dd>
            </div>

            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Original Created</dt>
              <dd>{originalCreated}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last Updated</dt>
              <dd>{new Date(data.updatedAt).toLocaleString()}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-700">Variants</h3>
            <span className="text-xs text-slate-400 ml-2">({data.variants?.length ?? 0} total)</span>
          </div>

          {data.variants?.length ? (
            <ul className="space-y-3">
              {data.variants.map((variant) => (
                <li key={variant.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-700">
                    {variant.name}{' '}
                    <span className="text-xs text-slate-400">(ERP {variant.erpId})</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-4 text-xs text-slate-500">
                    <span>Brand: {variant.brand?.name ?? '—'}</span>
                    <span>
                      Retail: {variant.retailPrice
                        ? `${variant.retailPrice} ${variant.currencyCode ?? ''}`
                        : '—'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-400">No variants linked to this master product.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MastersDetailPanel;
